import { supabase } from './supabase';
import { config } from './config';
const origin = config.communicationApi;
export type NotificationPrefs = {
  whatsapp_service: boolean;
  whatsapp_marketing: boolean;
  email_service: boolean;
  email_marketing: boolean;
  daily_summary: boolean;
};
export type Channel = 'push' | 'email' | 'whatsapp';
export type Topic = 'setup' | 'setup_changes' | 'billing' | 'daily_summary' | 'news';
export type ChannelSettings = Record<Channel, { enabled: boolean; topics: Partial<Record<Topic, boolean>> }>;
export type Connection = {
  linked: boolean;
  automatic_messages_enabled: boolean;
  /** Whether account alerts are going out; separate from the automatic sign-up messages above. */
  alerts_enabled: boolean;
  number: { normalized_address: string } | null;
  preferences: NotificationPrefs | null;
  email: string | null;
  email_verified: boolean;
  channels: ChannelSettings;
  /** What each channel can carry. */
  offered: Record<Channel, Topic[]>;
  push: { public_key: string | null; devices: number };
};
/** One channel's change: its switch, or some of its notifications. */
export type ChannelChange = Partial<Record<Channel, { enabled?: boolean; topics?: Partial<Record<Topic, boolean>> }>>;
export const saveChannels = (channels: ChannelChange) =>
  communication<{ saved: boolean; channels: ChannelSettings }>('/account/notifications', 'PATCH', { channels });
/**
 * Keeps a signup that stops before the account exists, as a lead beside the WhatsApp chats.
 * Fire and forget: it never slows or blocks the signup.
 */
export function saveSignupLead(lead: {
  email: string;
  name?: string;
  company?: string;
  phone?: string;
  company_size?: string;
}) {
  if (config.isDemo || config.isSandbox) return;
  fetch(origin + '/signup/lead', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(lead),
    signal: AbortSignal.timeout(15000),
  }).catch(() => undefined);
}
export async function communication<T>(path: string, method = 'GET', value?: unknown): Promise<T> {
  if (config.isDemo) throw new Error('WhatsApp account connection is available in your live workspace.');
  const { data } = await supabase.auth.getSession();
  if (!data.session) throw new Error('Please sign in again.');
  let r: Response;
  try {
    r = await fetch(origin + path, {
      method,
      headers: { Authorization: 'Bearer ' + data.session.access_token, 'Content-Type': 'application/json' },
      body: value === undefined ? undefined : JSON.stringify(value),
      signal: AbortSignal.timeout(15000),
    });
  } catch (error) {
    if (error instanceof Error && /timeout|abort/i.test(error.name))
      throw new Error('Notification settings took too long to respond. Please retry.');
    throw new Error('Couldn’t reach notification settings. Check your connection and retry.');
  }
  const json = await r.json().catch(() => {
    throw new Error('Notification settings are temporarily unavailable. Please retry.');
  });
  if (!r.ok) throw new Error(json.error || 'Could not update notifications.');
  return json as T;
}
export async function requestWhatsappConfirmation(
  number: string,
  trigger: 'manual' | 'automatic_signup' = 'manual',
) {
  return communication<{ queued: boolean }>('/account/whatsapp', 'POST', {
    number,
    confirm_service: true,
    trigger,
  });
}
